import { useEffect, useState } from 'react';

interface HeroProps {
  scrollY: number;
}

export default function Hero({ scrollY }: HeroProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [searchType, setSearchType] = useState<'rent' | 'sale'>('sale');

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <img 
          src="https://public.readdy.ai/ai/img_res/bc3418932f8d2271e0a7f0d7ebf7e136.jpg"
          alt="Accra Real Estate"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
      </div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h1 className="text-5xl lg:text-6xl xl:text-7xl font-semibold mb-6 leading-tight text-white">
              The Operating System for Real Estate in Ghana
            </h1>
            
            <p className="text-xl lg:text-2xl mb-10 leading-relaxed text-white/90">
              Buy, manage, value, and transact property — transparently and securely.
            </p>

            {/* Search Filter */}
            <div 
              className="p-6 rounded-2xl mb-8 transition-all duration-700"
              style={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(10px)',
                transitionDelay: '300ms'
              }}
            >
              <div className="flex flex-col sm:flex-row gap-4">
                {/* Rent/Sale Toggle */}
                <div className="flex gap-2 p-1 rounded-full" style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }}>
                  <button
                    onClick={() => setSearchType('sale')}
                    className={`px-6 py-2 rounded-full font-medium whitespace-nowrap transition-all duration-300 cursor-pointer ${
                      searchType === 'sale' ? 'text-white' : 'text-white/70'
                    }`}
                    style={{ 
                      backgroundColor: searchType === 'sale' ? '#1E4FFF' : 'transparent'
                    }}
                  >
                    For Sale
                  </button>
                  <button
                    onClick={() => setSearchType('rent')}
                    className={`px-6 py-2 rounded-full font-medium whitespace-nowrap transition-all duration-300 cursor-pointer ${
                      searchType === 'rent' ? 'text-white' : 'text-white/70'
                    }`}
                    style={{ 
                      backgroundColor: searchType === 'rent' ? '#1E4FFF' : 'transparent'
                    }}
                  >
                    For Rent
                  </button>
                </div>

                {/* Search Input */}
                <div className="flex-1 flex items-center gap-3 px-4 py-3 rounded-xl" style={{ backgroundColor: 'rgba(255, 255, 255, 0.25)' }}>
                  <i className="ri-search-line text-xl text-white/80"></i>
                  <input
                    type="text"
                    placeholder="Search location, property type..."
                    className="flex-1 bg-transparent border-none outline-none text-white placeholder-white/60 text-sm"
                  />
                </div>

                {/* Search Button */}
                <button 
                  className="px-8 py-3 rounded-xl font-medium text-white whitespace-nowrap transition-all duration-300 hover:shadow-xl hover:scale-105 cursor-pointer"
                  style={{ backgroundColor: '#1E4FFF' }}
                >
                  Search
                </button>
              </div>
            </div>

            <div className="flex flex-wrap gap-4">
              <button 
                className="px-8 py-4 rounded-xl font-medium text-white whitespace-nowrap transition-all duration-300 hover:shadow-xl hover:scale-105 cursor-pointer"
                style={{ backgroundColor: '#1E4FFF' }}
              >
                Get Started
              </button>
              
              <button 
                className="px-8 py-4 rounded-xl font-medium whitespace-nowrap transition-all duration-300 hover:shadow-lg cursor-pointer text-white"
                style={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(12px)',
                  border: '1px solid rgba(255, 255, 255, 0.4)'
                }}
              >
                View Listings
              </button>
            </div>
          </div>

          {/* Right Visual - Floating Glass Cards */}
          <div className="relative h-[600px] hidden lg:block">
            {/* Card 1 */}
            <div 
              className="absolute top-12 right-0 w-80 p-6 rounded-2xl transition-all duration-700"
              style={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(16px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                transform: `translateY(${scrollY * 0.05}px)`
              }}
            >
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-medium text-white/80">East Legon, Accra</span>
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#1E4FFF' }}></div>
              </div>
              <h3 className="text-3xl font-semibold mb-2 text-white">GH₵ 850,000</h3>
              <p className="text-sm mb-4 text-white/70">4 Bed • 3 Bath • 2,400 sqft</p>
              <div className="flex items-center gap-2">
                <span className="text-xs px-3 py-1 rounded-full" style={{ backgroundColor: 'rgba(30, 79, 255, 0.2)', color: '#fff' }}>
                  +12% ROI
                </span>
              </div>
            </div>

            {/* Card 2 */}
            <div 
              className="absolute top-64 right-24 w-72 p-6 rounded-2xl transition-all duration-700"
              style={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.18)',
                backdropFilter: 'blur(18px)',
                border: '1px solid rgba(255, 255, 255, 0.35)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                transform: `translateY(${scrollY * 0.08}px)`
              }}
            >
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-medium text-white/80">Airport Residential</span>
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#1E4FFF' }}></div>
              </div>
              <h3 className="text-3xl font-semibold mb-2 text-white">GH₵ 1.2M</h3>
              <p className="text-sm mb-4 text-white/70">5 Bed • 4 Bath • 3,200 sqft</p>
              <div className="flex items-center gap-2">
                <span className="text-xs px-3 py-1 rounded-full" style={{ backgroundColor: 'rgba(30, 79, 255, 0.2)', color: '#fff' }}>
                  Verified
                </span>
              </div>
            </div>

            {/* Card 3 */}
            <div 
              className="absolute top-96 right-48 w-64 p-5 rounded-2xl transition-all duration-700"
              style={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.12)',
                backdropFilter: 'blur(14px)',
                border: '1px solid rgba(255, 255, 255, 0.25)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
                transform: `translateY(${scrollY * 0.1}px)`
              }}
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-white/80">Cantonments</span>
              </div>
              <h3 className="text-2xl font-semibold mb-2 text-white">GH₵ 650,000</h3>
              <p className="text-xs text-white/70">3 Bed • 2 Bath • 1,800 sqft</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
